import {DTNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {
  DisconnectedNodes,
  NodeType,
  OutputType,
  type MapNode,
  type NodeOutputData,
} from 'common/src/modules/nodes/nodes.types';
import {ConditionCategory} from 'common/src/modules/conditions/conditions.types';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';

const OUTPUT_TYPE_ORDER: Record<number, number> = {
  [OutputType.Regular]: 1,
  [OutputType.PartialSuccess]: 2,
  [OutputType.Last]: 3,
  [OutputType.Fallback]: 4,
};

export function sortOutputsByType<T extends {type?: number}>(outputs: T[]): T[] {
  return [...outputs].sort((a, b) => {
    const orderA = OUTPUT_TYPE_ORDER[a.type ?? OutputType.Regular] ?? 999;
    const orderB = OUTPUT_TYPE_ORDER[b.type ?? OutputType.Regular] ?? 999;

    return orderA - orderB;
  });
}

export function formatOutputHeader(
  outputIndex: number,
  output: {id: number; name?: string | number | null},
): string {
  const namePart = output.name ? `, Name: ${output.name}` : '';

  return `Output index ${outputIndex} (Id: ${output.id}${namePart})`;
}

export function findFallbackCreativeNodeId(nodes: DisconnectedNodes): number | undefined {
  const fallbackNode = Object.values(nodes).find(
    (node) => (node as unknown as DTNode).isFallbackCreative,
  );

  return fallbackNode?.id;
}

export function getBaseHeader(card: MapNode): string {
  const typeName = cardTypeToName[card.type as NodeType] ?? 'Card';

  return `Card Id: ${card.id}
Name: ${card.name || `${typeName} card`}
Type: ${typeName}`;
}

export function getOutputIdentifier(output: {name?: string | number | null; id: number}): string {
  const namePart = output.name ? ` '${output.name}'` : '';

  return `${output.id}${namePart}`;
}

export function getDestinationPhrase(
  nodeId: number | undefined,
  fallbackCreativeNodeId?: number,
  outputType?: number,
): string {
  if (nodeId) {
    return `leads to card ${nodeId}`;
  }

  // Only suggest fallback creative for unconnected FALLBACK outputs
  if (fallbackCreativeNodeId && outputType === OutputType.Fallback) {
    return `leads to fallback card ${fallbackCreativeNodeId}`;
  }

  return "doesn't lead anywhere";
}

export function formatFallbackOutputLine(
  output: {id: number; name?: string | number | null; nodeId?: number; type?: number},
  fallbackCreativeNodeId?: number,
  whenClause?: string,
): string {
  const outputId = getOutputIdentifier(output);
  const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);
  const isUnconnected = !output.nodeId;
  const clause = whenClause ?? 'when none of the conditions above are met';

  const fallbackPart = isUnconnected ? `${dest}, active ${clause}` : `${dest} ${clause}`;

  return `Output ${outputId} (fallback) ${fallbackPart}.`;
}

export function formatShortOutputLine(
  output: {
    id: number;
    name?: string | number | null;
    nodeId?: number;
    type?: number;
  },
  fallbackCreativeNodeId?: number,
): string {
  const outputId = getOutputIdentifier(output);
  const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);
  const isFallback = output.type === OutputType.Fallback;

  if (isFallback) {
    return `Output ${outputId} (fallback) ${dest}.`;
  }

  return `Output ${outputId} ${dest}.`;
}

export function getOutputTypePhrase(output: NodeOutputData): string {
  switch (output.type) {
    case OutputType.Regular:
      return '(selection succeeded)';
    case OutputType.Fallback:
      return '(selection failed)';
    case OutputType.Last:
      return '(last output)';
    case OutputType.PartialSuccess:
      return '(partial success, not enough products selected)';
    default:
      return '(selection failed)';
  }
}

export function getCategoryUINamePart(signalMainCategory?: ConditionCategory): string {
  if (signalMainCategory == null) {
    return '';
  }

  const categoryName = ConditionCategory[signalMainCategory];

  return categoryName ? `${categoryName} ` : '';
}
